import {
	ApolloQueryResult,
	DocumentNode,
	WatchQueryFetchPolicy,
	useLazyQuery,
} from '@apollo/client';
import { map } from 'ramda';
import { useContext, useMemo } from 'react';
import { useDeepCompareEffect } from 'react-use';

import { doesNotExist } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import { useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { FolxMembershipStatus } from '@data/membership';

import { currentMember, currentMemberName, queryName } from '@graphql/fragments/currentMember';

export interface B2BCustomer {
	id: string;
	name: string;
}

export interface ICancelationReason {
	butterKey: string;
	cancellationReasonId: string;
}

export interface ICancelationReasons {
	cancellationReasons: {
		reasons: ICancelationReason[];
	};
}

export enum SubscriptionType {
	membership = 'FOLX_MEMBERSHIP',
}

export interface MemberInfo {
	email: string;
	stateOfResidence: {
		abbreviation: string;
		gahtTestosterone: boolean;
		gahtEstrogen: boolean;
		mentalHealthTherapy: boolean;
		mentalHealthMedication: boolean;
		virtualHealthcare: boolean;
		adhd: boolean;
	};
	id: string;
	hasValidSsn: boolean;
	displayNameFirst?: string;
	displayNameLast?: string;
	b2bCustomer?: B2BCustomer;
}

export interface SubscriptionInfo {
	membershipSubscription: MembershipSubscription;
}

export enum SubscriptionPaidFor {
	// Subscription paid for by employer
	EMPLOYER = 'EMPLOYER',
	// Subscription paid for by a waiver
	WAIVER = 'WAIVER',
	// Subscription paid for by insurance partnership
	INSURANCE = 'INSURANCE',
	// Subscription paid for out of pocket
	OUT_OF_POCKET = 'OUT_OF_POCKET',
}

export interface MembershipSubscription {
	type: SubscriptionType;
	status: keyof typeof FolxMembershipStatus;
	upcomingInvoice: {
		total: number;
		nextPaymentAttempt: string;
	};
	canceledAt?: string;
	cancelAt?: string;
	isTrial?: boolean;
	billingInterval?: 'MONTH' | 'YEAR' | 'NONE';
	nextSubscriptionRenewalDate?: string;
	paidFor?: SubscriptionPaidFor;
}

export interface B2BMember {
	b2b?: {
		kind?: B2BMemberKind;
		customer?: B2BCustomer;
	};
}

export enum B2BMemberKind {
	primary = 'PRIMARY',
	dependent = 'DEPENDENT',
}

export interface ILegalName {
	legalFirstName: string;
	legalLastName: string;
}

export interface IMultiFactorConfiguration {
	multiFactorConfiguration: {
		smsEnrolled: boolean;
		smsRequired: boolean;
		totpEnrolled: boolean;
		totpRequired: boolean;
	};
}

export interface ISmsNumber {
	id: string;
	smsNumber: string;
	smsContactConsent: boolean;
}

export interface IMemberInterest {
	memberFolxInterestID: string;
	name: string;
	value: boolean;
}

export interface IInterest {
	id: string;
	name: string;
	listOrderAsc: number;
}

export interface IInterestData {
	getInterests: {
		nodes: IInterest[];
	};
}

export interface IMemberInterestData {
	interests: {
		nodes: IMemberInterest[];
	};
}

interface ICurrentMember<T> {
	authenticatedMember: MemberInfo & T;
}

export interface IMemberSubscription<T> {
	membershipSubscription: T;
}

export interface IAuthenticatedMember<T> {
	authenticatedMember: T;
}

export interface IFragment {
	name: string;
	node: DocumentNode;
}

type UseMemberInfo = <T>(
	additionalFragments?: IFragment[],
	fetchPolicy?: WatchQueryFetchPolicy
) => {
	loading: boolean;
	memberInfo?: MemberInfo & T;
	refetchMemberInfo: () => Promise<ApolloQueryResult<ICurrentMember<T>>>;
};
const useMemberInfo: UseMemberInfo = <T,>(
	additionalFragments: IFragment[] = [],
	fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
	const { initialized } = useAuth();

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const names = useMemo(() => map((fragment) => fragment.name, additionalFragments), []);
	const nodes = useMemo(() => map((fragment) => fragment.node, additionalFragments), []);

	const memberQuery = useMemo(() => {
		const combinedFragments = combineMemberFragments(
			[...names, currentMemberName],
			[...nodes, currentMember],
			queryName
		);
		return combinedFragments;
	}, [names, nodes]);

	const [
		getMemberInfo,
		{ loading: loading, error: activeError, data: activeData, refetch: refetch },
	] = useLazyQuery<ICurrentMember<T>>(memberQuery, {
		client: graphQLv2Client,
		fetchPolicy,
	});

	useDeepCompareEffect(() => {
		if (doesNotExist(memberQuery) || !initialized) {
			return;
		}

		getMemberInfo();
	}, [memberQuery, initialized]);

	const memberInfo: (MemberInfo & T) | undefined = useMemo(() => {
		if (loading) return undefined;

		return activeData?.authenticatedMember;
	}, [loading, activeData]);

	return {
		loading: loading,
		memberInfo,
		refetchMemberInfo: refetch,
	};
};

export default useMemberInfo;
